import React from "react";
import cn from "./helpers/cn";
import { TableContext, } from "./Table";
import { TableVariantContext, } from "./TableHead";

type CellVariant = "head" | "body" | "footer";
type CellPadding = "normal" | "checkbox" | "none";
type CellAlign = "inherit" | "left" | "center" | "right" | "justify";

interface TableCellProps extends Omit<React.TdHTMLAttributes<HTMLTableCellElement>, "align"> {
  component?: React.ElementType;
  variant?: CellVariant;
  align?: CellAlign;
  padding?: CellPadding;
  size?: "small" | "medium";
  sortDirection?: "asc" | "desc" | false;
  scope?: string;
  children?: React.ReactNode;
}

const alignMap: Record<CellAlign, string> = {
  inherit: "",
  left: "text-left",
  center: "text-center",
  right: "text-right",
  justify: "text-justify",
};

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>((props, ref) => {
  const {
    className,
    component,
    variant: variantProp,
    align = "inherit",
    padding: paddingProp,
    size: sizeProp,
    sortDirection,
    scope: scopeProp,
    children,
    ...other
  } = props;

  const tableCtx = React.useContext(TableContext);
  const variantCtx = React.useContext(TableVariantContext);

  const variant: CellVariant = variantProp ?? variantCtx;
  const size = sizeProp ?? tableCtx.size;
  const padding: CellPadding = paddingProp ?? "normal";

  const isHead = variant === "head";
  const Component = component ?? (isHead ? "th" : "td");

  // scope padrão "col" para th no head
  const scope = scopeProp ?? (isHead && Component === "th" ? "col" : undefined);

  // Padding base (MUI v4: padding 16px) e variantes
  const paddingClass =
    padding === "none" ? "p-0"
      : padding === "checkbox" ? "w-12 px-1 py-0"
        : size === "small" ? "px-4 py-1.5"
          : "px-4 py-4";

  return (
    <Component
      ref={ref}
      scope={scope}
      aria-sort={sortDirection ? (sortDirection === "asc" ? "ascending" : "descending") : undefined}
      className={cn(
        "border-b border-slate-200 text-sm",
        paddingClass,
        isHead
          ? "font-medium leading-6 text-slate-900"
          : variant === "footer"
            ? "text-xs text-slate-500"
            : "text-slate-700",
        tableCtx.stickyHeader && isHead && "sticky top-0 z-10 bg-white",
        alignMap[align],
        className
      )}
      {...other}
    >
      {children}
    </Component>
  );
});

TableCell.displayName = "TableCell";

export default TableCell;
